/**
 * New Year clipart — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearClipartPack: ToilePack = {
    slug: 'new-year-clipart',
    category: 'season',
    name: 'New Year clipart',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'flute', label: 'Champagne flute', subject: 'A champagne flute with bubbles, isolated on a plain white ground'},
        {key: 'burst', label: 'Firework burst', subject: 'A single firework burst with trailing sparks, isolated on a plain white ground'},
        {key: 'horn', label: 'Party horn', subject: 'A striped party horn with a curl of streamer, isolated on a plain white ground'},
    ],
    styles: [
        {key: 'deco', label: 'Art deco', style: 'art deco illustration, geometric fans and sunbursts, clean lines, no outlines', palette: 'black and gold with a little cream', framing: 'Centered on a solid black background with an even margin'},
        {key: 'neon', label: 'Neon party', style: 'neon party graphic, glowing lines, confetti and streamers', palette: 'electric pink, cyan and gold on deep navy', framing: 'Centered on a dark background, built to read clean at small size'},
        {key: 'minimal', label: 'Minimal', style: 'modern minimal illustration, few flat shapes, generous space', palette: 'gold and white with a single black', framing: 'Centered on a plain white background with generous space around it'},
    ],
    directions: ['a confetti cannon instead', 'in gold foil', 'as a small icon set'],
    directionHint: 'try: a confetti cannon instead',
    seo: {
        title: 'New Year Clipart: Champagne, Fireworks, Party Horn',
        metaDescription: 'Nine new year clipart pieces — champagne flute, firework burst, party horn — isolated on white, in three styles.',
        h1: 'New year clipart: champagne, fireworks, party horn',
        tagline: 'Nine cutouts, three midnight subjects, three styles each.',
        intro: 'Clean, isolated clipart for the January send: a champagne flute, a firework burst, a party horn, each set flat on white so it drops straight into a newsletter, flyer, or card. Ask for a clock, a confetti pile, or your own subject, and the same three styles carry it.',
        useCases: [
            'a January newsletter header',
            'a printed invite or flyer',
            'a scrapbook or card layout',
            'a school or office bulletin board',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine clipart pieces — a champagne flute, a firework burst, a party horn — isolated on a plain white background, in three styles.'},
            {q: 'Is the background actually transparent?', a: 'The renders are flat on white, not a transparent PNG — cutting the background out for a transparent layer is a quick extra step in most editors.'},
            {q: 'Can I ask for a different subject, like a clock or confetti?', a: 'Yes — the direction field takes any new year subject, including a clock, a confetti pile, or a champagne bottle.'},
            {q: 'What size or format do I get?', a: 'Each piece renders as a single square image, sized for a screen or a standard print — resize freely for a flyer, sticker, or web banner.'},
            {q: "Can I use this for a print I'm selling?", a: 'Yes — check the license terms on your account for the specifics of commercial use.'},
        ],
    },
}
